import React, { useState } from 'react';
import { Box, Typography, Dialog, TextField, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as SuccessTick } from '../../assets/icons/SuccessTick.svg';
import ApiButton from '../UIControls/api-button/api-button';
import PinkGradientButton from '../UIControls/PinkGradientButton';
import { useSendShortFeedback } from './use-send-short-feedback';
import { notifyAuthExpired, isAuthExpiredError, SESSION_REFRESH_MESSAGE } from '../../utils/auth-error-utils';

// Each star's path from stars.svg, with the correct viewBox to show only that star.
// The gradient uses gradientUnits="userSpaceOnUse" spanning x=0..815 so the
// pink→purple gradient is correctly distributed across all 5 stars.
const STARS = [
    {
        viewBox: '0 0 142 138',
        path: 'M72.2982 0.993729L92.7919 43.1094C93.0487 43.6379 93.5395 43.9979 94.1134 44.0821L139.941 50.8372C141.383 51.0516 141.957 52.8438 140.915 53.87L107.751 86.6573C107.335 87.0633 107.147 87.653 107.245 88.2351L115.075 134.532C115.324 135.988 113.814 137.098 112.531 136.409L71.5431 114.551C71.0296 114.275 70.418 114.275 69.912 114.551L28.9246 136.409C27.6409 137.098 26.1307 135.988 26.3798 134.532L34.2104 88.2351C34.3085 87.6606 34.1197 87.0709 33.7044 86.6573L0.532355 53.87C-0.5097 52.8438 0.064185 51.0439 1.50645 50.8372L47.3342 44.0821C47.9081 43.9979 48.3989 43.6302 48.6556 43.1094L69.1494 0.993729C69.7912 -0.331243 71.6488 -0.331243 72.2982 0.993729Z',
    },
    {
        viewBox: '167 0 143 138',
        path: 'M237.539 0.993729L217.046 43.1094C216.789 43.6379 216.298 43.9979 215.724 44.0821L169.896 50.8372C168.454 51.0516 167.88 52.8438 168.922 53.87L202.087 86.6573C202.502 87.0633 202.691 87.653 202.593 88.2351L194.762 134.532C194.513 135.988 196.023 137.098 197.307 136.409L238.294 114.551C238.808 114.275 239.42 114.275 239.926 114.551L280.913 136.409C282.197 137.098 283.707 135.988 283.458 134.532L275.627 88.2351C275.529 87.6606 275.718 87.0709 276.133 86.6573L309.298 53.87C310.34 52.8438 309.766 51.0439 308.324 50.8372L262.496 44.0821C261.922 43.9979 261.431 43.6302 261.174 43.1094L240.688 0.993729C240.046 -0.331243 238.189 -0.331243 237.539 0.993729Z',
    },
    {
        viewBox: '336 0 143 138',
        path: 'M405.926 0.993729C405.927 0.993729 405.928 0.995552 405.928 0.997119L385.436 43.1094C385.179 43.6379 384.688 43.9979 384.114 44.0821L338.286 50.8372C336.844 51.0516 336.27 52.8438 337.312 53.87L370.477 86.6573C370.892 87.0633 371.081 87.653 370.983 88.2351L363.152 134.532C362.903 135.988 364.413 137.098 365.697 136.409L406.685 114.551C407.198 114.275 407.81 114.275 408.316 114.551L449.303 136.409C450.587 137.098 452.097 135.988 451.848 134.532L444.017 88.2351C443.919 87.6606 444.108 87.0709 444.523 86.6573L477.688 53.87C478.73 52.8438 478.156 51.0439 476.714 50.8372L430.886 44.0821C430.312 43.9979 429.821 43.6302 429.564 43.1094L409.071 0.993729C408.429 -0.330095 406.574 -0.331242 405.924 0.990288C405.923 0.991854 405.924 0.993729 405.926 0.993729Z',
    },
    {
        viewBox: '504 0 143 138',
        path: 'M574.316 0.993729C574.317 0.993729 574.318 0.995562 574.318 0.997137L553.826 43.1094C553.569 43.6379 553.078 43.9979 552.504 44.0821L506.676 50.8372C505.234 51.0516 504.66 52.8438 505.702 53.87L538.867 86.6573C539.282 87.0633 539.471 87.653 539.373 88.2351L531.542 134.532C531.293 135.988 532.803 137.098 534.087 136.409L575.074 114.551C575.588 114.275 576.2 114.275 576.706 114.551L617.693 136.409C618.977 137.098 620.487 135.988 620.238 134.532L612.407 88.2351C612.309 87.6606 612.498 87.0709 612.913 86.6573L646.078 53.87C647.12 52.8438 646.546 51.0439 645.104 50.8372L599.276 44.0821C598.702 43.9979 598.211 43.6302 597.954 43.1094L577.461 0.993729C576.819 -0.330089 574.964 -0.331242 574.314 0.99027C574.313 0.991844 574.314 0.993729 574.316 0.993729Z',
    },
    {
        viewBox: '673 0 143 138',
        path: 'M742.706 0.993729C742.707 0.993729 742.708 0.995552 742.708 0.997119L722.216 43.1094C721.959 43.6379 721.468 43.9979 720.894 44.0821L675.066 50.8372C673.624 51.0516 673.05 52.8438 674.092 53.87L707.257 86.6573C707.672 87.0633 707.861 87.653 707.763 88.2351L699.932 134.532C699.683 135.988 701.193 137.098 702.477 136.409L743.465 114.551C743.978 114.275 744.59 114.275 745.096 114.551L786.083 136.409C787.367 137.098 788.877 135.988 788.628 134.532L780.797 88.2351C780.699 87.6606 780.888 87.0709 781.303 86.6573L814.468 53.87C815.51 52.8438 814.936 51.0439 813.494 50.8372L767.666 44.0821C767.092 43.9979 766.601 43.6302 766.344 43.1094L745.851 0.993729C745.209 -0.330095 743.354 -0.331242 742.704 0.990288C742.703 0.991854 742.704 0.993729 742.706 0.993729Z',
    },
];

function StarRating({ rating, hoverRating, onRate, onHover, onLeave }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                height: { xs: '40px', md: '136px' },
            }}
        >
            {STARS.map((star, i) => {
                const n = i + 1;
                const active = n <= (hoverRating || rating);
                return (
                    <Box
                        key={n}
                        onClick={() => onRate(n)}
                        onMouseEnter={() => onHover(n)}
                        onMouseLeave={onLeave}
                        sx={{
                            width: { xs: '38px', md: '142px' },
                            flexShrink: 0,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            component="svg"
                            viewBox={star.viewBox}
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            sx={{ width: '100%', height: 'auto', maxHeight: { xs: '40px', md: '136px' } }}
                            preserveAspectRatio="xMidYMid meet"
                        >
                            {active && (
                                <defs>
                                    <linearGradient
                                        id={`sg-${n}`}
                                        x1="0"
                                        y1="0"
                                        x2="815"
                                        y2="0"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop offset="0%" stopColor="#FF669B" />
                                        <stop offset="100%" stopColor="#BD8DE8" />
                                    </linearGradient>
                                </defs>
                            )}
                            <path d={star.path} fill={active ? `url(#sg-${n})` : '#E0E0E0'} />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

export function FeedbackModal({ open, onClose }) {
    const sendShortFeedback = useSendShortFeedback();
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [statusDialog, setStatusDialog] = useState({
        open: false,
        isSuccess: true,
        title: '',
        message: '',
        buttonText: 'Close',
    });

    const handleClose = () => {
        setRating(0);
        setHoverRating(0);
        setComment('');
        setStatusDialog((prev) => ({ ...prev, open: false }));
        onClose();
    };

    const isActive = rating > 0;

    const submitFeedback = async () => {
        await sendShortFeedback({
            rating,
            additionalFeedback: comment.trim() || null,
        });
    };

    const openSuccessDialog = () => {
        setStatusDialog({
            open: true,
            isSuccess: true,
            title: 'Feedback submitted',
            message: 'Your feedback has been submitted successfully.',
            buttonText: 'Close',
        });
    };

    const openErrorDialog = (error) => {
        if (isAuthExpiredError(error)) {
            notifyAuthExpired({ source: 'feedback-modal' });
            return;
        }

        const fallbackMessage = 'There was a problem submitting your feedback. Please try again.';
        const message =
            error?.message === 'Failed to send feedback' ? fallbackMessage : error?.message || fallbackMessage;

        setStatusDialog({
            open: true,
            isSuccess: false,
            title: 'Feedback submission failed',
            message: message === SESSION_REFRESH_MESSAGE ? fallbackMessage : message,
            buttonText: 'Close',
        });
    };

    const handleStatusDialogClose = () => {
        if (statusDialog.isSuccess) {
            handleClose();
            return;
        }

        setStatusDialog((prev) => ({ ...prev, open: false }));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: { xs: '20px', md: '30px' },
                    padding: { xs: '16px', md: '30px' },
                    width: { xs: 'calc(100vw - 64px)', md: '895.379px' },
                    maxWidth: { xs: 'calc(100vw - 64px)', md: '895.379px' },
                    height: { xs: 'auto', md: '667px' },
                    maxHeight: { xs: 'calc(100dvh - 64px)', md: '667px' },
                    boxSizing: 'border-box',
                    position: 'relative',
                    overflow: { xs: 'auto', md: 'hidden' },
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Close button */}
            <IconButton
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    top: { xs: '13px', md: '20px' },
                    right: { xs: '13px', md: '20px' },
                    color: '#000453',
                    p: { xs: '4px', md: '8px' },
                }}
            >
                <CloseIcon sx={{ fontSize: { xs: '16px', md: '24px' } }} />
            </IconButton>

            {/* Title */}
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '26px', md: '42px' },
                    lineHeight: { xs: '26px', md: '42px' },
                    letterSpacing: { xs: '-1.04px', md: '-1.68px' },
                    color: '#000453',
                    mb: { xs: '6px', md: '12px' },
                    pr: { xs: '24px', md: '44px' },
                    flexShrink: 0,
                }}
            >
                Share your feedback
            </Typography>

            {/* Subtitle — 25px gap to stars below */}
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', md: '24px' },
                    lineHeight: { xs: '16px', md: '24px' },
                    letterSpacing: { xs: '-0.28px', md: '-0.96px' },
                    color: '#616063',
                    mb: { xs: '14px', md: '25px' },
                    flexShrink: 0,
                }}
            >
                How would you rate your experience using Cetra for Vets?
            </Typography>

            {/* Stars — 33px gap below to "Additional feedback" */}
            <Box sx={{ flexShrink: 0, height: { xs: '40px', md: '136px' }, mb: { xs: '18px', md: '33px' } }}>
                <StarRating
                    rating={rating}
                    hoverRating={hoverRating}
                    onRate={setRating}
                    onHover={setHoverRating}
                    onLeave={() => setHoverRating(0)}
                />
            </Box>

            {/* Additional feedback label — 29px gap to textarea */}
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', md: '24px' },
                    lineHeight: { xs: '16px', md: '24px' },
                    letterSpacing: { xs: '-0.28px', md: '-0.96px' },
                    color: '#616063',
                    mb: { xs: '12px', md: '29px' },
                    flexShrink: 0,
                }}
            >
                Additional feedback (optional)
            </Typography>

            {/* Text area */}
            <TextField
                multiline
                fullWidth
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell us what worked well or what we could improve…"
                InputProps={{
                    sx: {
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '12px', md: '20px' },
                        letterSpacing: { xs: '-0.24px', md: '-0.8px' },
                        color: '#616063',
                        borderRadius: { xs: '10px', md: '20px' },
                        height: { xs: '136px', md: '183px' },
                        alignItems: 'flex-start',
                        '& fieldset': { borderColor: '#E5E5E5', borderWidth: '1.5px' },
                        '&:hover fieldset': { borderColor: '#E5E5E5' },
                        '&.Mui-focused fieldset': { borderColor: '#FF669B' },
                    },
                }}
                inputProps={{
                    sx: { '&::placeholder': { opacity: 0.5, color: '#616063' } },
                }}
                sx={{ mb: { xs: '16px', md: '30px' }, flexShrink: 0 }}
            />

            {/* Submit button — 211×56px, border-radius 30px */}
            <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <ApiButton
                    apiCall={submitFeedback}
                    buttonLabel="Submit"
                    buttonVariant="pink"
                    disabled={!isActive}
                    onSuccess={openSuccessDialog}
                    onError={openErrorDialog}
                    showSuccessDialog={false}
                    sx={{
                        m: 0,
                        width: { xs: '100%', md: 'auto' },
                    }}
                    buttonStyle={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '14px', md: '20px' },
                        letterSpacing: { xs: '-0.28px', md: '-0.4px' },
                        color: '#FFFFFF',
                        backgroundImage: 'none',
                        backgroundColor: '#FF669B',
                        border: 'none',
                        borderRadius: { xs: '17px', md: '30px' },
                        width: { xs: '100%', md: '211px' },
                        height: { xs: '34px', md: '56px' },
                        marginY: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': { backgroundColor: '#FF669B', opacity: 0.85 },
                    }}
                />
            </Box>

            <Dialog
                open={statusDialog.open}
                onClose={handleStatusDialogClose}
                PaperProps={{
                    sx: {
                        borderRadius: { xs: '13.425px', md: '28px' },
                        width: { xs: 358, md: 750 },
                        maxWidth: 750,
                        px: { xs: 2.5, md: 8 },
                        pt: { xs: 3, md: 5 },
                        pb: { xs: 3, md: 5 },
                        textAlign: 'center',
                    },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, md: 2 } }}>
                    {statusDialog.isSuccess ? (
                        <Box component={SuccessTick} sx={{ width: { xs: 52, md: 104 }, height: { xs: 52, md: 104 } }} />
                    ) : (
                        <Box
                            sx={{
                                width: { xs: 52, md: 104 },
                                height: { xs: 52, md: 104 },
                                borderRadius: '50%',
                                border: { xs: '2px solid #EB5757', md: '4px solid #EB5757' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CloseIcon sx={{ color: '#EB5757', fontSize: { xs: 28, md: 56 } }} />
                        </Box>
                    )}
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: '22px', md: '36px' },
                        fontWeight: 600,
                        color: '#000453',
                        letterSpacing: { xs: '-0.88px', md: '-1.44px' },
                        lineHeight: { xs: '22px', md: '36px' },
                        mb: '16px',
                    }}
                >
                    {statusDialog.title}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '10px', md: '20px' },
                        fontWeight: 400,
                        lineHeight: { xs: '10px', md: '20px' },
                        color: '#616063',
                        letterSpacing: { xs: '-0.4px', md: '-0.8px' },
                        mb: { xs: '21px', md: '42.01px' },
                    }}
                >
                    {statusDialog.message}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <PinkGradientButton
                        onClick={handleStatusDialogClose}
                        sx={{
                            width: { xs: '99px', md: '197.732px' },
                            height: { xs: '28px', md: '52.267px' },
                            fontSize: { xs: '14px', md: '16px' },
                            fontWeight: 500,
                            letterSpacing: { xs: '-0.56px', md: '-0.32px' },
                            borderRadius: { xs: '13.425px', md: '28px' },
                            marginY: 0,
                        }}
                    >
                        {statusDialog.buttonText}
                    </PinkGradientButton>
                </Box>
            </Dialog>
        </Dialog>
    );
}

export default FeedbackModal;
